/**
 * AI Tutor session tracking API client.
 * Uses same fetch + getTutorHeaders pattern as TeacherLive.tsx.
 * All functions are best-effort: errors are caught silently so tracking
 * failures never crash or block the tutor UI.
 */

import type {
  SessionStartIn,
  SessionStartOut,
  SessionPingIn,
  SessionSummaryIn,
  TutorSummaryOut,
} from '../types/tracking';
import { serviceUrls } from '../../config/serviceUrls';

const AI_TUTOR_BASE =
  import.meta.env.VITE_AI_TUTOR_BASE_URL || serviceUrls.gateway;

const TUTOR_API_PREFIX = AI_TUTOR_BASE.includes('ai-tutor') ? '/tutor' : '/v1/tutor';

/** Absolute URL for session tracking paths (e.g. `/session/summary`) — used by sendBeacon in hooks. */
export function tutorTrackingAbsolute(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${AI_TUTOR_BASE}${TUTOR_API_PREFIX}${p}`;
}

function getTutorHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || '';
  const tenantId = localStorage.getItem('X_TENANT_ID') || '';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['x-tenant-id'] = tenantId;

  return headers;
}

export async function startTutorSession(
  payload: SessionStartIn
): Promise<SessionStartOut | null> {
  try {
    const res = await fetch(`${AI_TUTOR_BASE}${TUTOR_API_PREFIX}/session/start`, {
      method: 'POST',
      headers: getTutorHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn('[tutorTracking] startSession failed:', res.status);
      return null;
    }
    return (await res.json()) as SessionStartOut;
  } catch (e) {
    console.warn('[tutorTracking] startSession error:', e);
    return null;
  }
}

/** NOTE: SessionPingIn uses `ts` field (not `timestamp`) */
export async function pingTutorSession(payload: SessionPingIn): Promise<void> {
  try {
    const res = await fetch(`${AI_TUTOR_BASE}${TUTOR_API_PREFIX}/session/ping`, {
      method: 'POST',
      headers: getTutorHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn('[tutorTracking] ping failed:', res.status);
    }
  } catch {
    // silent — ping failures are acceptable
  }
}

export async function submitTutorSummary(payload: SessionSummaryIn): Promise<void> {
  try {
    const res = await fetch(`${AI_TUTOR_BASE}${TUTOR_API_PREFIX}/session/summary`, {
      method: 'POST',
      headers: getTutorHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn('[tutorTracking] summary failed:', res.status);
    }
  } catch {
    // silent
  }
}

export async function getMyTutorSummary(days = 30): Promise<TutorSummaryOut | null> {
  try {
    const res = await fetch(
      `${AI_TUTOR_BASE}${TUTOR_API_PREFIX}/analytics/me/summary?days=${days}`,
      { headers: getTutorHeaders() }
    );
    if (!res.ok) return null;
    return (await res.json()) as TutorSummaryOut;
  } catch {
    return null;
  }
}

export async function getStudentTutorSummary(
  studentId: string,
  days = 30
): Promise<TutorSummaryOut | null> {
  try {
    const res = await fetch(
      `${AI_TUTOR_BASE}${TUTOR_API_PREFIX}/analytics/student/${studentId}/summary?days=${days}`,
      { headers: getTutorHeaders() }
    );
    if (!res.ok) return null;
    return (await res.json()) as TutorSummaryOut;
  } catch {
    return null;
  }
}
